import { useMemo, useState } from "react";
import { Button, Form, Icon, Input, Label, Message, Modal, Popup, Segment } from "semantic-ui-react";
import { createColumnHelper } from "@tanstack/react-table";
import GuestHouseDropdown from "./GuestHouseDropdown";
import DataTable from "./DataTable";
import { minutesLeft, roomLabel } from "./adminHelpers";
import { compareRoomNumbers } from "../../utils/roomOrder";

const ROOM_TYPES = ["Single", "Double", "AC Single", "AC Double", "Suite"];
const EMPTY_ROOM = {
  guestHouseId: "",
  guestHouseName: "",
  roomNumber: "",
  roomType: "Single",
  floor: "",
  status: "available",
  maintenanceRemark: "",
};

const columnHelper = createColumnHelper();

const fieldError = (message) => (message ? { content: message, pointing: "below" } : false);

const normalizeRoom = (f) => ({
  guestHouseId: f.guestHouseId,
  roomNumber: f.roomNumber || "",
  roomType: f.roomType || "",
  floor: f.floor || "",
  status: f.status || "",
  maintenanceRemark: f.status === "maintenance" ? (f.maintenanceRemark || "") : "",
});

export default function RoomsTab({ rooms, guestHouses, roomTypes, onAdd, onUpdate, onDelete }) {
  const [roomModal, setRoomModal] = useState(null);
  const [form, setForm] = useState(EMPTY_ROOM);
  const [originalForm, setOriginalForm] = useState(EMPTY_ROOM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [ghFilter, setGhFilter] = useState("All");
  const [maintenancePrompt, setMaintenancePrompt] = useState(null);
  const [maintenanceRemarkInput, setMaintenanceRemarkInput] = useState("");
  const [maintenanceSubmitting, setMaintenanceSubmitting] = useState(false);

  const filtered = useMemo(() => {
    const q = searchInput.trim().toLowerCase();
    return [...rooms]
      .filter((r) => ghFilter === "All" || Number(r.guestHouseId) === Number(ghFilter))
      .filter((r) =>
        !q ||
        (r.guestHouseName || "").toLowerCase().includes(q) ||
        (r.roomType || "").toLowerCase().includes(q) ||
        (r.roomNumber || "").toLowerCase().includes(q) ||
        (r.floor || "").toLowerCase().includes(q) ||
        (r.status || "").toLowerCase().includes(q),
      )
      .sort(
        (a, b) =>
          a.guestHouseId - b.guestHouseId ||
          compareRoomNumbers(a.roomNumber, b.roomNumber),
      );
  }, [rooms, searchInput, ghFilter]);

  const openAdd = () => {
    const defaultGh = guestHouses[0];
    setForm({
      ...EMPTY_ROOM,
      guestHouseId: defaultGh?.id || "",
      guestHouseName: defaultGh?.name || "",
    });
    setErrors({});
    setRoomModal({ mode: "add" });
  };
  const openEdit = (r) => {
    setForm({ ...r });
    setOriginalForm({ ...r });
    setErrors({});
    setRoomModal({ mode: "edit", id: r.id });
  };

  const updateForm = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const dirty =
    roomModal?.mode !== "edit" ||
    JSON.stringify(normalizeRoom(form)) !== JSON.stringify(normalizeRoom(originalForm));

  /* Price lives on the room type now (RoomType.base_price) — every room of a
     type shares the same price, so just look it up directly instead of
     approximating from existing rooms. */
  const getRoomTypePrice = (ghId, typeName) => {
    const type = (roomTypes?.[ghId] || []).find((t) => t.name === typeName);
    const price = type?.basePrice ?? type?.base_price;
    return price || null;
  };

  const roomTypeOptions = (roomTypes?.[form.guestHouseId] || []).length
    ? roomTypes[form.guestHouseId].map((t) => t.name)
    : ROOM_TYPES;

  const validate = () => {
    const e = {};
    if (!form.guestHouseId) e.guestHouseId = "Select a guest house.";
    if (!form.roomNumber.trim()) e.roomNumber = "Room number is required.";
    if (!form.roomType) e.roomType = "Select a room type.";
    if (!form.floor.trim()) e.floor = "Floor is required.";
    return e;
  };

  const handleSave = async () => {
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      if (roomModal.mode === "add") {
        const gh = guestHouses.find((g) => g.id === Number(form.guestHouseId));
        await onAdd({
          ...form,
          guestHouseName: gh?.name || form.guestHouseName,
          guestHouseId: Number(form.guestHouseId),
          floor: form.floor || "",
          isAC: form.roomType.startsWith("AC") || form.roomType === "Suite",
        });
      } else {
        await onUpdate(roomModal.id, {
          ...form,
          guestHouseId: Number(form.guestHouseId),
          floor: form.floor || "",
          isAC: form.roomType.startsWith("AC") || form.roomType === "Suite",
        });
      }
      setRoomModal(null);
    } catch (e) {
      setErrors({ save: e.message || "Failed to save room." });
    } finally {
      setSaving(false);
    }
  };

  const handleGhChange = (ghId) => {
    const gh = guestHouses.find((g) => g.id === Number(ghId));
    setForm((f) => ({
      ...f,
      guestHouseId: Number(ghId),
      guestHouseName: gh?.name || "",
    }));
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError("");
    try {
      await onDelete(deleteConfirm.id);
      setDeleteConfirm(null);
    } catch (e) {
      setDeleteError(e.message || "Failed to delete room.");
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor("guestHouseName", { header: "Guest House" }),
      columnHelper.accessor("roomType", { header: "Type" }),
      columnHelper.accessor("roomNumber", {
        header: "Room No.",
        cell: (info) => {
          const r = info.row.original;
          return roomLabel(info.getValue(), r.guestHouseShortName);
        },
      }),
      columnHelper.accessor("floor", {
        header: "Floor",
        cell: (info) => info.getValue() || "—",
      }),
      columnHelper.accessor("pricePerNight", {
        header: "Price/Night",
        cell: (info) => (info.getValue() ? `₹${info.getValue()}` : "—"),
      }),
      columnHelper.accessor("status", {
        header: "Status",
        cell: (info) => {
          const r = info.row.original;
          const label = (
            <Label color={info.getValue() === "available" ? "green" : "red"}>
              {info.getValue() === "maintenance" ? "Maintenance" : "Available"}
            </Label>
          );
          const remaining = minutesLeft(r.maintenanceUntil);
          const popupContent =
            info.getValue() === "maintenance"
              ? `${r.maintenanceRemark || "No remark"}${remaining ? ` — ~${remaining} min left` : ""}`
              : "";
          return info.getValue() === "maintenance" ? (
            <Popup trigger={label} content={popupContent} />
          ) : (
            label
          );
        },
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        enableSorting: false,
        cell: (info) => {
          const r = info.row.original;
          return (
            <Button.Group size="small">
              <Button basic onClick={() => openEdit(r)}>
                <Icon name="edit" />
                Edit
              </Button>
              <Button
                basic
                onClick={() => {
                  if (r.status === "maintenance") {
                    onUpdate(r.id, { status: "available" });
                  } else {
                    setMaintenanceRemarkInput("");
                    setMaintenancePrompt(r);
                  }
                }}
              >
                <Icon name="wrench" />
                {r.status === "maintenance" ? "Mark Available" : "Maintenance"}
              </Button>
              <Button
                basic
                negative
                onClick={() => {
                  setDeleteError("");
                  setDeleteConfirm(r);
                }}
              >
                <Icon name="trash" />
                Delete
              </Button>
            </Button.Group>
          );
        },
      }),
    ],
    [onUpdate],
  );

  return (
    <div>
      <Segment basic vertical>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Input
              icon="search"
              iconPosition="left"
              placeholder="Search by room no., type, guest house, floor, status…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              action={
                searchInput
                  ? { icon: "times circle", basic: true, onClick: () => setSearchInput("") }
                  : undefined
              }
              style={{ minWidth: 340 }}
            />
            {guestHouses.length > 1 && (
              <GuestHouseDropdown
                value={ghFilter}
                options={[
                  { value: "All", label: "All Guest Houses" },
                  ...guestHouses.map((g) => ({ value: g.id, label: g.name })),
                ]}
                onChange={(v) => setGhFilter(v === "All" ? "All" : Number(v))}
              />
            )}
          </div>
          <Button primary onClick={openAdd}>
            <Icon name="plus" />
            Add Room
          </Button>
        </div>
      </Segment>

      <DataTable
        data={filtered}
        columns={columns}
        batchSize={20}
        emptyMessage={rooms.length === 0 ? "No rooms found." : "No rooms match your filters."}
      />

      {/* Add / Edit Modal */}
      <Modal
        open={!!roomModal}
        onClose={() => setRoomModal(null)}
        size="small"
        closeIcon
      >
        <Modal.Header>
          <Icon name={roomModal?.mode === "add" ? "plus circle" : "edit"} />
          {roomModal?.mode === "add" ? "Add New Room" : "Edit Room"}
        </Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Group widths="equal">
              <Form.Field required error={!!errors.guestHouseId}>
                <label>Guest House</label>
                <GuestHouseDropdown
                  value={form.guestHouseId}
                  options={guestHouses.map((g) => ({ value: g.id, label: g.name }))}
                  onChange={(ghId) => handleGhChange(ghId)}
                />
                {errors.guestHouseId && (
                  <Label basic color="red" pointing content={errors.guestHouseId} style={{ marginTop: 6 }} />
                )}
              </Form.Field>
              <Form.Input
                label="Room Number"
                required
                placeholder="e.g. 101"
                value={form.roomNumber}
                error={fieldError(errors.roomNumber)}
                onChange={(e) => updateForm("roomNumber", e.target.value)}
              />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Select
                label="Room Type"
                required
                value={form.roomType}
                options={roomTypeOptions.map((t) => {
                  const price = getRoomTypePrice(form.guestHouseId, t);
                  return {
                    key: t,
                    value: t,
                    text: price ? `${t}   ₹${price}` : t,
                  };
                })}
                error={fieldError(errors.roomType)}
                onChange={(_, data) => updateForm("roomType", data.value)}
              />
              <Form.Input
                label="Floor"
                required
                placeholder="e.g. 1, G, 2"
                value={form.floor}
                error={fieldError(errors.floor)}
                onChange={(e) => updateForm("floor", e.target.value)}
              />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Select
                label="Status"
                value={form.status}
                options={[
                  { key: "available", value: "available", text: "Available" },
                  { key: "maintenance", value: "maintenance", text: "Maintenance" },
                ]}
                onChange={(_, data) => updateForm("status", data.value)}
              />
            </Form.Group>
            {form.status === "maintenance" && (
              <Form.TextArea
                label="Maintenance Remark (optional)"
                placeholder="e.g. AC repair in progress"
                value={form.maintenanceRemark || ""}
                onChange={(e) => updateForm("maintenanceRemark", e.target.value)}
              />
            )}
          </Form>
          {errors.save && <Message negative content={errors.save} />}
        </Modal.Content>
        <Modal.Actions>
          <Button basic onClick={() => setRoomModal(null)} disabled={saving}>
            Cancel
          </Button>
          <Button primary loading={saving} disabled={saving || !dirty} onClick={handleSave}>
            <Icon name="save" />
            {saving ? "Saving…" : roomModal?.mode === "add" ? "Add Room" : "Save Changes"}
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Delete Confirm */}
      <Modal
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        size="mini"
      >
        <Modal.Header>
          <Icon name="trash" /> Delete Room
        </Modal.Header>
        <Modal.Content>
          <p>
            Delete room <strong>{deleteConfirm?.roomNumber}</strong>?
          </p>
          <p className="bhu-confirm-sub">This cannot be undone.</p>
          {deleteError && <Message negative content={deleteError} />}
        </Modal.Content>
        <Modal.Actions>
          <Button basic onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button negative loading={deleting} disabled={deleting} onClick={handleDelete}>
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Mark Under Maintenance — optional remark */}
      <Modal
        open={!!maintenancePrompt}
        onClose={() => setMaintenancePrompt(null)}
        size="mini"
      >
        <Modal.Header>
          <Icon name="wrench" /> Mark Room Under Maintenance
        </Modal.Header>
        <Modal.Content>
          <p>
            Mark room <strong>{maintenancePrompt?.roomNumber}</strong> under maintenance?
          </p>
          <Form>
            <Form.TextArea
              label="Remark (optional)"
              placeholder="e.g. AC repair in progress"
              value={maintenanceRemarkInput}
              onChange={(e) => setMaintenanceRemarkInput(e.target.value)}
            />
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button basic onClick={() => setMaintenancePrompt(null)} disabled={maintenanceSubmitting}>
            Cancel
          </Button>
          <Button
            negative
            loading={maintenanceSubmitting}
            disabled={maintenanceSubmitting}
            onClick={async () => {
              setMaintenanceSubmitting(true);
              try {
                await onUpdate(maintenancePrompt.id, {
                  status: "maintenance",
                  maintenanceRemark: maintenanceRemarkInput,
                });
                setMaintenancePrompt(null);
              } finally {
                setMaintenanceSubmitting(false);
              }
            }}
          >
            {maintenanceSubmitting ? "Saving…" : "Mark Under Maintenance"}
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
}
